/**
 * Company Research progress-stream contract (architecture.md §4, §7).
 *
 * Shared between the api (SSE producer, E09-F02-T03 helper + E06-F02
 * orchestrator) and the web SPA (EventSource consumer, `research.sse.ts`).
 * The wire transport is SSE over `GET /research/stream/:request_id?token=…`;
 * this module defines the *named events* and their JSON payloads, so producer
 * and consumer share one source of truth and never drift.
 *
 * Event taxonomy (the `event:` field on the SSE frame):
 *   - `progress`  — periodic snapshot of `research_jobs` counters while running.
 *   - `stage`     — emitted when the orchestrator advances `current_stage`.
 *   - `completed` — terminal: the run finished (or paused for review).
 *   - `failed`    — terminal: the run failed.
 *   - `cancelled` — terminal: the run was cancelled.
 *   - `error`     — non-terminal transport/notice (the stream stays open).
 *
 * The job state machine itself (`queued → running → paused → completed`,
 * `+ failed/cancelled`) lives in `research_jobs.status` (database-schema §3.4);
 * the terminal SSE events mirror that machine's absorbing states.
 *
 * Heartbeats are SSE comment frames (`: keep-alive`), NOT named events — they
 * carry no payload and exist only to keep proxies from dropping an idle socket.
 */
import { RESEARCH_STAGES } from './contracts/research-pipeline.js';
/**
 * SSE-side per-company pipeline stages — the WORKING stages a `stage` event can
 * report, i.e. the canonical `RESEARCH_STAGES` (research-pipeline.ts, the
 * `research_companies.stage` machine) MINUS the terminal `'failed'` (a failed row
 * surfaces as the dedicated `failed` SSE event, never as a `stage` notice).
 *
 * DISTINCT NAME on purpose: the canonical `RESEARCH_STAGES`/`ResearchStage`
 * (7 values, incl. `'failed'`) is owned by `research-pipeline.ts` and consumed by
 * the api worker/upsert. This SSE subset carries its own name so the two value
 * sets never collide in the shared barrel (collision resolved by renaming, not
 * by force-merging different sets).
 */
export const RESEARCH_SSE_STAGES = RESEARCH_STAGES.filter((s) => s !== 'failed');
/** Job state machine (research_jobs.status, database-schema §3.4). */
export const RESEARCH_JOB_STATUSES = [
    'queued',
    'running',
    'paused',
    'completed',
    'failed',
    'cancelled',
];
/** SSE event names for the research stream. */
export const RESEARCH_STREAM_EVENTS = [
    'progress',
    'stage',
    'completed',
    'failed',
    'cancelled',
    'error',
];
/** Terminal event names — after one of these the producer closes the stream. */
export const RESEARCH_TERMINAL_EVENTS = [
    'completed',
    'failed',
    'cancelled',
];
/** Type guard: is this a terminal event after which the stream closes? */
export function isResearchTerminalEvent(name) {
    return RESEARCH_TERMINAL_EVENTS.includes(name);
}
//# sourceMappingURL=researchStream.js.map